import React, { useState } from 'react';
import { Upload, GitCompareArrows, TrendingUp, TrendingDown, Sparkles, XCircle, Loader2, MousePointerClick, Eye, Scale, Shuffle } from 'lucide-react';
import { KpiCard } from './_shared';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => (Math.abs(n) >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${Math.round(n * 10) / 10}`);

const FileDrop = ({ label, hint, file, onFile, testId }) => (
  <label
    className={`flex-1 flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
      file ? 'border-teal-400 bg-teal-50/50' : 'border-gray-200 hover:border-gray-300 bg-white'
    }`}
    data-testid={testId}
  >
    <Upload className={`w-5 h-5 ${file ? 'text-teal-500' : 'text-gray-400'}`} />
    <span className="text-sm font-medium text-gray-700">{label}</span>
    <span className="text-xs text-gray-400">{file ? file.name : hint}</span>
    <input type="file" accept=".csv" className="hidden" onChange={(e) => onFile(e.target.files?.[0] || null)} />
  </label>
);

const DeltaCell = ({ value, invert = false, suffix = '' }) => {
  const good = invert ? value > 0 : value > 0;
  const color = value === 0 ? 'text-gray-400' : good ? 'text-emerald-600' : 'text-red-500';
  return (
    <span className={`font-semibold ${color}`}>
      {value > 0 ? '+' : ''}{fmt(value)}{suffix}
    </span>
  );
};

const DiffTable = ({ title, icon: Icon, color, rows, dimension, testId, mode = 'both' }) => {
  if (!rows?.length) return null;
  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden" data-testid={testId}>
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
        <Icon className="w-4 h-4" style={{ color }} />
        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
        <span className="text-xs text-gray-400 ml-auto">{rows.length} shown</span>
      </div>
      <div className="overflow-x-auto max-h-96 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-gray-50 text-gray-500">
            <tr>
              <th className="text-left px-4 py-2 font-medium">{dimension}</th>
              <th className="text-right px-3 py-2 font-medium">Clicks A→B</th>
              <th className="text-right px-3 py-2 font-medium">Δ Clicks</th>
              <th className="text-right px-3 py-2 font-medium">Δ Impr.</th>
              <th className="text-right px-3 py-2 font-medium">Pos A→B</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-gray-50 hover:bg-gray-50/60">
                <td className="px-4 py-2 text-gray-700 max-w-xs truncate" title={r.key}>{r.key}</td>
                <td className="px-3 py-2 text-right text-gray-500 whitespace-nowrap">
                  {mode === 'new' ? '—' : fmt(r.clicks_a)} → {mode === 'lost' ? '—' : fmt(r.clicks_b)}
                </td>
                <td className="px-3 py-2 text-right"><DeltaCell value={r.clicks_delta} /></td>
                <td className="px-3 py-2 text-right"><DeltaCell value={r.impressions_delta} /></td>
                <td className="px-3 py-2 text-right text-gray-500 whitespace-nowrap">
                  {r.position_a ? r.position_a.toFixed(1) : '—'} → {r.position_b ? r.position_b.toFixed(1) : '—'}
                  {r.position_delta !== 0 && <span className="ml-1"><DeltaCell value={r.position_delta} invert /></span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function GscDiffTab() {
  const [fileA, setFileA] = useState(null);
  const [fileB, setFileB] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [minClicks, setMinClicks] = useState(0);

  const compare = async () => {
    if (!fileA || !fileB) return;
    setLoading(true);
    setError('');
    try {
      const form = new FormData();
      form.append('file_a', fileA);
      form.append('file_b', fileB);
      const resp = await fetch(`${API_URL}/api/admin/gsc-diff`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${sessionStorage.getItem('admin_token')}` },
        body: form,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.detail || 'Comparison failed');
      setResult(data);
    } catch (e) {
      setError(e.message);
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const filterRows = (rows) => rows?.filter((r) => Math.max(r.clicks_a, r.clicks_b) >= minClicks);
  const s = result?.summary;

  return (
    <div className="space-y-5" data-testid="gsc-diff-tab">
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex items-center gap-2 mb-1">
          <GitCompareArrows className="w-4 h-4 text-teal-500" />
          <h2 className="text-sm font-bold text-gray-900">GSC Winners &amp; Losers</h2>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Export the same table (Queries or Pages) from Search Console → Performance for two periods, then compare.
          Period A = older, Period B = newer.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <FileDrop label="Period A (older)" hint="e.g. previous 28 days CSV" file={fileA} onFile={setFileA} testId="gsc-file-a" />
          <FileDrop label="Period B (newer)" hint="e.g. last 28 days CSV" file={fileB} onFile={setFileB} testId="gsc-file-b" />
        </div>
        <div className="flex items-center gap-3 mt-4">
          <button
            onClick={compare}
            disabled={!fileA || !fileB || loading}
            className="px-5 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg text-xs font-semibold hover:opacity-90 disabled:opacity-40 flex items-center gap-2"
            data-testid="gsc-compare-btn"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <GitCompareArrows className="w-3.5 h-3.5" />}
            Compare Periods
          </button>
          {result && (
            <label className="flex items-center gap-2 text-xs text-gray-500">
              Min clicks:
              <input
                type="number"
                min="0"
                value={minClicks}
                onChange={(e) => setMinClicks(Number(e.target.value) || 0)}
                className="w-16 px-2 py-1 border border-gray-200 rounded text-gray-700"
                data-testid="gsc-min-clicks"
              />
            </label>
          )}
          {error && <span className="text-xs text-red-500" data-testid="gsc-error">{error}</span>}
        </div>
      </div>

      {s && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="gsc-summary">
            <KpiCard icon={MousePointerClick} color="#14b8a6" label="Clicks A → B" value={`${fmt(s.clicks_a)} → ${fmt(s.clicks_b)}`} subtext={`${s.clicks_delta > 0 ? '+' : ''}${fmt(s.clicks_delta)} clicks`} />
            <KpiCard icon={Eye} color="#0ea5e9" label="Impressions A → B" value={`${fmt(s.impressions_a)} → ${fmt(s.impressions_b)}`} subtext={`${s.impressions_delta > 0 ? '+' : ''}${fmt(s.impressions_delta)}`} />
            <KpiCard icon={Scale} color="#8b5cf6" label="Winners / Losers" value={`${s.winners} / ${s.losers}`} subtext={`${result.dimension}s with click movement`} />
            <KpiCard icon={Shuffle} color="#f59e0b" label="New / Lost" value={`${s.new} / ${s.lost}`} subtext="appeared / disappeared in period B" />
          </div>

          <div className="grid lg:grid-cols-2 gap-5">
            <DiffTable title="Winners (clicks up)" icon={TrendingUp} color="#059669" rows={filterRows(result.winners)} dimension={result.dimension} testId="gsc-winners" />
            <DiffTable title="Losers (clicks down)" icon={TrendingDown} color="#EF4444" rows={filterRows(result.losers)} dimension={result.dimension} testId="gsc-losers" />
            <DiffTable title="New in period B" icon={Sparkles} color="#0EA5E9" rows={filterRows(result.new_entries)} dimension={result.dimension} testId="gsc-new" mode="new" />
            <DiffTable title="Lost from period A" icon={XCircle} color="#9CA3AF" rows={filterRows(result.lost_entries)} dimension={result.dimension} testId="gsc-lost" mode="lost" />
          </div>
        </>
      )}
    </div>
  );
}
